import { create } from "zustand";
import { jqXHR, ResponseError } from "../types/Api";
import Api from "../utils/Api";
import { AgentConfig, AgentNetworkConfig, LegacyAgentConfig } from "../types/AgentConfig";

export const AGENT_CONFIG_URL = "/config/ajax/fzagentconfig";

export interface AgentConfigStore {
  config: AgentConfig;
  configRetrieved: boolean;
  getOrFetch: () => Promise<AgentConfig>;
  fetch: () => Promise<AgentConfig>;
  save: (config: AgentConfig) => Promise<AgentConfig>;
  reset: () => void;
  set: (config: AgentConfig) => void;
}

const synchronized: { fetchResult?: Promise<AgentConfig> } = {};

const getDefaultAgentConfig = (): AgentConfig => {
  return {
    on_network: getDefaultNetworkConfig(),
    off_network: getDefaultNetworkConfig(),
    auto_set_policies: {
      companion: {
        enabled: false,
      },
      content_aware: {
        enabled: false,
      },
    },
    exclude_domains: [],
  };
};

const getDefaultNetworkConfig = (): AgentNetworkConfig => {
  return {
    filtering_enabled: false,
    default_filtering_mode: "web",
    filtering_groups: {
      bypass: [],
      dns: [],
      web: [],
      full: [],
      companion: [],
    },
    mitm_enabled: false,
    mitm_inclusions: {
      websites: [],
      signatures: [],
      website_objects: [],
    },
    mitm_exclude_groups: [],
    safe_search: {
      bing: {
        enabled: false,
      },
      google: {
        enabled: false,
      },
      youtube: {
        enabled: false,
        mode: "MODERATE",
      },
    },
    classroom_enabled: false,
  };
};

const defaultSafeSearchConfig = () => {
  return {
    google: {
      enabled: false,
    },
    bing: {
      enabled: false,
    },
    youtube: {
      enabled: false,
      mode: "MODERATE",
    },
  };
};

/**
 * Gets Agent config from the store, or if the store is empty, then fetches
 * the config from Config Management API first.
 *
 * @returns agent config promise.
 */
export const useAgentConfigStore = create<AgentConfigStore>((set, get) => ({
  config: getDefaultAgentConfig(),
  configRetrieved: false,

  getOrFetch: async (): Promise<AgentConfig> => {
    let config = get().config;
    const configRetrieved = get().configRetrieved;

    if (configRetrieved) {
      return config;
    }

    if (synchronized.fetchResult) {
      return await synchronized.fetchResult;
    }

    synchronized.fetchResult = get().fetch();
    config = await synchronized.fetchResult;
    synchronized.fetchResult = undefined;

    return config;
  },

  fetch: async (): Promise<AgentConfig> => {
    let responseConfig = {} as LegacyAgentConfig,
      statusCode = 200;

    await Api.getAsync(AGENT_CONFIG_URL).then(
      (response: AgentConfig) => (responseConfig = response),
      (reason: jqXHR) => (statusCode = reason?.status)
    );

    if (statusCode !== 200) {
      throw new ResponseError("Failed to fetch mobile agent Config", statusCode);
    }

    const config = getDefaultAgentConfig();

    // TODO:
    // We are handling legacy config here.
    // Check if this can be removed safely
    if (responseConfig.mitm_enabled) {
      config.on_network.mitm_enabled = true;
      config.off_network.mitm_enabled = true;
    }
    if (responseConfig.mitm_disabled_outside_network_enabled) {
      config.off_network.mitm_enabled = false;
    }
    if (responseConfig.mitm_groups) {
      config.on_network.filtering_groups.web = responseConfig.mitm_groups;
    }
    if (responseConfig.mitm_disabled_outside_network_groups) {
      config.off_network.filtering_groups.web = responseConfig.mitm_disabled_outside_network_groups;
    }
    // Check new config next
    if (responseConfig.on_network) {
      config.on_network = responseConfig.on_network;

      if (!config.on_network.safe_search) {
        config.on_network.safe_search = defaultSafeSearchConfig();
      }

      if (!config.on_network.filtering_groups.bypass) {
        config.on_network.filtering_groups.bypass = [];
      }

      if (!config.on_network.filtering_groups.companion) {
        config.on_network.filtering_groups.companion = [];
      }
    }
    if (responseConfig.off_network) {
      config.off_network = responseConfig.off_network;

      if (!config.off_network.safe_search) {
        config.off_network.safe_search = defaultSafeSearchConfig();
      }

      if (!config.off_network.filtering_groups.bypass) {
        config.off_network.filtering_groups.bypass = [];
      }

      if (!config.off_network.filtering_groups.companion) {
        config.off_network.filtering_groups.companion = [];
      }
    }
    if (!!responseConfig.exclude_domains) {
      config.exclude_domains = responseConfig.exclude_domains;
    }

    if (!!responseConfig.auto_set_policies) {
      config.auto_set_policies = responseConfig.auto_set_policies;
    }

    set({ config, configRetrieved: true });

    return config;
  },

  set: (config: AgentConfig): void => {
    set({ config });
  },

  save: async (config: AgentConfig): Promise<AgentConfig> => {
    let saved: AgentConfig | undefined,
      statusCode = 200;

    await Api.postAsync(AGENT_CONFIG_URL, config).then(
      () => (saved = { ...get().config, ...config }),
      (reason: jqXHR) => (statusCode = reason?.status)
    );

    if (!saved || statusCode !== 200) {
      throw new ResponseError("Failed to save mobile agent Config", statusCode);
    }

    set({ config: saved });

    return saved;
  },

  reset: () => {
    set({ config: getDefaultAgentConfig(), configRetrieved: false });
  },
}));
